"use client";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Rating, Stack, Typography } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE } from "../../constants/Variables";
import { calculateProductPricing } from "../../utils";
import { APP_IMAGES } from "../../assets";

const PackageCard = ({
    cardData,
    cardStyleProps,
    selectable = false,
    isSelected = false,
    onToggleSelect,
    showRemove = false,
    onRemove,
}) => {
    const { currentLanguage } = useSelector((state) => state.languageReducer);
    const settings = useSelector((state) => state.settingsReducer.settings);
    const vat = settings.tax || DEFAULT_VAT_PERCENTAGE;

    const finalPrice = cardData.isAllowedOrder ? cardData.effectivePackagePrice : cardData.packagePrice;

    const productPricing = calculateProductPricing(
        finalPrice,
        0,
        0
    );

    const handleCardClick = () => {
        if (selectable && onToggleSelect) {
            onToggleSelect(cardData);
        }
    };

    const handleRemoveClick = (e) => {
        e.stopPropagation();
        onRemove?.(cardData);
    };

    return (
        <Box
            onClick={handleCardClick}
            sx={{
                height: "100%",
                textDecoration: "none",
                position: "relative",
                overflow: "hidden",
                cursor: selectable ? "pointer" : "default",
                "&:hover": {
                    ".card-container": {
                        backgroundColor: "background.white",
                        boxShadow: "0px 4px 20px 0px #0000001A",
                    },
                },
                ...cardStyleProps,
            }}
        >
            <Box
                className="card-container"
                position="relative"
                overflow="hidden"
                bgcolor="background.white"
                borderRadius={2}
                p={1}
                border={1}
                borderColor={isSelected ? "primary.main" : "divider"}
                sx={{
                    boxShadow: isSelected ? (theme) => `0 0 0 1px ${theme.palette.primary.main}` : "none",
                }}
            >
                {selectable && isSelected && (
                    <CheckCircleIcon
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 2,
                            color: "primary.main",
                            fontSize: 22,
                        }}
                    />
                )}

                {showRemove && (
                    <IconButton
                        size="small"
                        aria-label="remove package"
                        onClick={handleRemoveClick}
                        sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            zIndex: 2,
                            bgcolor: "background.paper",
                            boxShadow: 1,
                            "&:hover": {
                                bgcolor: "error.light",
                                color: "error.contrastText",
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                <Box
                    height={{ xs: 150, sm: 180 }}
                    borderRadius={2}
                    bgcolor="text.white"
                    overflow="hidden"
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    p={0.5}
                >
                    <Box
                        component={"img"}
                        src={cardData?.coverImage}
                        alt={cardData?.name[currentLanguage]}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = APP_IMAGES.noImageAvailable;
                        }}
                        sx={{
                            height: { xs: 150, sm: 180 },
                            aspectRatio: 16 / 9,
                            width: "100%",
                            objectFit: "contain",
                            loading: "lazy",
                        }}
                    />
                </Box>

                <Stack mt={0.8} direction="row" alignItems="center" gap={0.5}>
                    <Rating
                        name="size-medium"
                        size="small"
                        value={cardData?.rating}
                        readOnly
                    />
                    <Typography variant="body2" color="text.secondary">
                        ({cardData?.rating})
                    </Typography>
                </Stack>

                <Typography
                    mt={0.5}
                    fontSize="1rem"
                    fontWeight={500}
                    color={"text.black"}
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {cardData.name[currentLanguage]}
                </Typography>

                <Typography
                    mt={0.8}
                    lineHeight={1.3}
                    fontSize={{ xs: "0.75rem", md: "0.875rem" }}
                    color="text.secondary"
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {cardData?.description ? cardData?.description[currentLanguage] : ""}
                </Typography>

                <Stack mt={0.8}>
                    <Typography
                        fontSize={{ xs: "1rem", md: "1.125rem" }}
                        color="primary.main"
                        fontWeight={600}
                    >
                        {`${productPricing.productPriceWithVat} ${CURRENCY}`}
                    </Typography>
                </Stack>


            </Box>
        </Box>
    );
};

export default React.memo(PackageCard);
